import React, { forwardRef, useState, useContext, useEffect } from "react";
import Modal from "../../components/Modal";
import Button from "../../components/Button";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toDatePickerValue } from "../../utility/datePickerValue";
import { AppContext } from "../../context/app.context";
import { ModelCxContext } from "../../context/modelCx.context";
import { ScheduleContext } from "../../context/schedule.context";
import { MILLISECONDS_PER_DAY, RESCHEDULE_REASON_OPTIONS } from "../../utility/keys";
import {
  computeMultiDayJobRecommendation,
  computeRecommendedEstComplete,
} from "../../utility/multiDayJobRecommendation";
import { isCapacityAwareEnabled } from "../../utility/isCapacityAwareEnabled";
import { isComplianceHeld, COMPLIANCE_HOLD_ALERT } from "../../utility/complianceHold";
import {
  isArrivalRequiredButMissing,
  isArrivalTimeMissing,
} from "../../utility/arrivalRequired";

// - - - - - - - - - -

/**
 * Pure function that computes the new-value set for a drag-n-drop operation.
 *
 * Contract:
 * - MUST NOT mutate `foundDocument`, `dropObject`, or `weekDatesArray`.
 * - Returns an object containing ONLY the fields that drag-n-drop wants to
 *   change for this drop (date_scheduled, date_estimatedCompletion,
 *   installer, statusCode, and when applicable extraCellId/extraCellText).
 *
 * This was extracted (#85) because the previous implementation mutated
 * `foundDocument` directly (a live TanStack cache object). Downstream
 * change-detection in processDragNDrop compared `updateValues` against the
 * same mutated object, so every field looked unchanged and the payload
 * shipped with only history/_mongoUsername — the exact silent-data-loss
 * failure mode reported after v04-10-2026-04.
 *
 * @param {Object} params
 * @param {Object} params.foundDocument - Read-only. Must not be mutated.
 * @param {{ id: string, content?: any }} params.dropObject - The drop target.
 * @param {string[]} params.weekDatesArray - Week dates (Mon..Sun) as strings.
 * @returns {Object} new values — keys intentionally omitted when not changed.
 */
export function computeJobInitValues({ foundDocument, dropObject, weekDatesArray }) {
  // Day difference between original scheduled date and original est completion
  // — preserved across the move so a multi-day job stays the same length.
  // Computed WITHOUT mutating foundDocument; if est completion is missing,
  // fall back to scheduled date locally.
  let dayDifference = 0;
  const origDateScheduled = foundDocument.date_scheduled;
  if (origDateScheduled && origDateScheduled !== "") {
    const origEstComp =
      foundDocument.date_estimatedCompletion &&
      foundDocument.date_estimatedCompletion !== ""
        ? foundDocument.date_estimatedCompletion
        : origDateScheduled;
    const timeDifference =
      new Date(origEstComp).getTime() - new Date(origDateScheduled).getTime();
    dayDifference = Math.round(timeDifference / (1000 * 3600 * 24));
  }

  // Drop ID formats (#105 phase 5 cross-grid fix):
  //   Optimizer crew cell:    "<payrollNo>_<columnIndex>_<dateString>"
  //   Optimizer unassigned:   "Unassigned_<DayName>_<dateString>"
  //   Legacy Scheduler crew:  "<payrollNo>_<columnIndex>"
  //   Legacy Scheduler unass: "Unassigned_<DayName>"
  //   RTS:                    "Unassigned_RTS"
  // The trailing date is required for multi-grid Optimizer because the
  // user may drop on a grid whose week differs from AppContext's
  // weekDatesArray. When absent (legacy Scheduler), fall back to
  // weekDatesArray[columnIndex] which is correct because that tab is
  // single-week.
  const dropParts = dropObject.id.split("_");
  const droppedPayrollNo = dropObject.id.slice(0, 7);
  const droppedColumnIndex = dropObject.id.slice(8);
  const extraCellId = dropObject.content?.props?.project?.id;
  const extraCellCustomText =
    dropObject.content?.props?.project?.encodedSmallTitle;

  // - - - - Unassigned-row drops (bottom row: Unassigned_Mon..Sun, Unassigned_RTS)
  if (dropObject.id.slice(0, 10) === "Unassigned") {
    if (dropObject.id === "Unassigned_RTS") {
      return {
        date_scheduled: "",
        date_estimatedCompletion: "",
        installer: "",
        statusCode: 3,
        extraCellId: null,
        extraCellText: null,
      };
    }
    const dayIndexByDayName = {
      Mon: 0,
      Tue: 1,
      Wed: 2,
      Thr: 3,
      Fri: 4,
      Sat: 5,
      Sun: 6,
    };
    const dayName = dropParts[1];
    const dayIdx = dayIndexByDayName[dayName];
    if (dayIdx === undefined) {
      // Unknown day — no changes.
      return {};
    }
    // dropParts[2..] is the date when present (multi-grid Optimizer).
    // Slice + join because dates contain "/", not "_" — but in case the
    // format ever changes, joining is safe.
    const encodedDate = dropParts.length >= 3 ? dropParts.slice(2).join("_") : null;
    const dateScheduled = encodedDate || weekDatesArray[dayIdx];
    const dateEstComp = new Date(
      Date.parse(dateScheduled) + MILLISECONDS_PER_DAY * dayDifference
    ).toLocaleDateString("en-us");
    return {
      date_scheduled: dateScheduled,
      date_estimatedCompletion: dateEstComp,
      installer: "",
      statusCode: 4,
      extraCellId,
      extraCellText: extraCellCustomText,
    };
  }

  // - - - - Crew-cell drop (assigning to a specific installer on a specific day)
  // dropParts = [payrollNo, columnIndex, dateString?]
  const encodedDate = dropParts.length >= 3 ? dropParts.slice(2).join("_") : null;
  const dateScheduled = encodedDate || weekDatesArray[droppedColumnIndex];
  const dateEstComp = new Date(
    Date.parse(dateScheduled) + MILLISECONDS_PER_DAY * dayDifference
  ).toLocaleDateString("en-us");
  return {
    date_scheduled: dateScheduled,
    date_estimatedCompletion: dateEstComp,
    installer: droppedPayrollNo,
    statusCode: 4,
  };
}

// - - - - - - - - - -

function JobInitModal({ setOpenJobInitModal }) {
  const {
    companyCode,
    weekDatesArray,
    dataFormatting_payrollFields,
    locationFilter,
    openJobInitModal,
  } = useContext(AppContext);
  const {
    tsCreateExtraCellAttribute,
    tsRemoveExtraCellAttribute,
    projectListAfterMerge,
    projectListAfterMergeLocation,
    awsgwCandidatesResult,
    awsgwCandidatesResultLocation,
  } = useContext(ModelCxContext);
  const { setProcessingDragNDrop, dragObject, dropObject, setPendingDragUpdate, isJobParked } =
    useContext(ScheduleContext);

  // Use TanStack data directly (Realm EOL Jan 13, 2026)
  // Only use data if it matches current location (prevents stale data on location switch)
  const canUseTanstackProjects = projectListAfterMergeLocation === locationFilter;
  const canUseTanstackCandidates = awsgwCandidatesResultLocation === locationFilter;
  const projectsList = canUseTanstackProjects ? projectListAfterMerge : [];
  const candidatesList = canUseTanstackCandidates ? awsgwCandidatesResult : [];
  const [jobDocument, setJobDocument] = useState({});
  const [currentEstComplete, setCurrentEstComplete] = useState();
  const [currentArrivalTime, setCurrentArrivalTime] = useState();
  const [rescheduleReason, setRescheduleReason] = useState("");
  const [isRescheduling, setIsRescheduling] = useState(false);
  // #193 — over-capacity multi-day advisory (read-only). Populated in the
  // mount effect when assigning a job whose square footage exceeds the target
  // installer's once/day capacity; null otherwise.
  const [multiDayRec, setMultiDayRec] = useState(null);
  const [installerName, setInstallerName] = useState("");

  const todayDate = new Date();
  // initial date is tomorrow
  todayDate.setDate(todayDate.getDate() + 1);

  // - - - - - - - - - -

  const onSave = () => {
    // required field validation
    // #261 — arrival_time_text is REQUIRED whenever a job is being scheduled
    // (statusCode 4). The old check `currentArrivalTime === ""` let an
    // untouched dropdown through: currentArrivalTime starts as useState() ===
    // undefined, and `undefined !== ""`, so a job got scheduled with an empty
    // arrival (the webhook in #261). isArrivalRequiredButMissing treats
    // null/undefined/""/whitespace all as missing.
    if (
      isArrivalRequiredButMissing({
        statusCode: jobDocument.statusCode,
        arrival: currentArrivalTime,
      })
    ) {
      window.alert("Arrival is required before Saving!");
    } else if (isRescheduling && rescheduleReason === "") {
      window.alert("Reason is required when rescheduling!");
    } else {
      // Pass the updated values to processDragNDrop via pendingDragUpdate.
      // Mutation-free: use values from local state/jobDocument (a local copy)
      // rather than mutating the TanStack document (#85).
      setPendingDragUpdate({
        installer: jobDocument.installer,
        date_scheduled: jobDocument.date_scheduled,
        date_estimatedCompletion: currentEstComplete,
        arrival_time_text: currentArrivalTime,
        statusCode: jobDocument.statusCode,
        extraCellId: jobDocument.extraCellId,
        extraCellText: jobDocument.extraCellText,
        ...(isRescheduling && rescheduleReason ? { rescheduleReason } : {}),
      });

      // will trigger processDragNDrop() (scheduleContext)
      setProcessingDragNDrop(true);

      // Close modal window
      setOpenJobInitModal({ show: false });
    }
  };

  // - - - - - - - - - -

  const onEstCompleteChange = (date) => {
    const fmDate = new Date(Date.parse(date)).toLocaleDateString("en-us");
    setCurrentEstComplete(fmDate);
    console.log(
      "JobInitModal onEstCompleteChange, date: ",
      date,
      ", fmDate: ",
      fmDate
    );
  };

  // - - - - - - - - - -

  const onArrivalChange = (arrival) => {
    setCurrentArrivalTime(arrival);
    console.log("JobInitModal onArrivalChange, arrival: ", arrival);
  };

  // - - - - - - - - - -

  const EstCompleteInput = forwardRef(({ value, onClick, className }, ref) => (
    <Button
      color="clear"
      className={`${className}`}
      refInner={ref}
      onClick={onClick}
    >
      <div className="flex items-center">
        <span>{value}</span>
        <span className="ml-1 arrow arrow-down arrow-small"></span>
      </div>
    </Button>
  ));

  // - - - - - - - - - -

  useEffect(() => {
    // Find the dropped document in the TanStack projects list.
    // IMPORTANT (#85): we MUST NOT mutate this document. It is the same
    // object reference that processDragNDrop will read for change detection.
    const foundDocument = projectsList.find((p) => p.id === dragObject);
    if (!foundDocument) {
      // this should never happen, but just in case...
      console.log(
        "JobInitModal useEffect[] ERROR couldn't find project, dragObject: ",
        dragObject
      );
      setOpenJobInitModal({ show: false });
      window.alert("Drag failed!");
      return;
    }
    console.log("JobInitModal useEffect[] foundDocument: ", foundDocument);

    // - - - - Compliance check (JIRA SW-710, updated 2025-01-28 for date-based hold)
    // Scheduling_Hold_Effective_Date replaces earlier Scheduling_Hold_Flag.
    // #352 — now via the shared isComplianceHeld predicate (same rule as the
    // schedule-context hard block, the icon, and the reassign-block). This stays
    // as a defense-in-depth backstop; the primary hard block is in
    // processDragNDrop, which runs before this modal opens.
    const droppedPayrollNo = dropObject.id.slice(0, 7);
    const droppedColumnIndex = dropObject.id.slice(8);
    const installerDocument = candidatesList.find(
      (document) =>
        dataFormatting_payrollFields(document.payrollNo) === droppedPayrollNo
    );
    // Only check compliance when assigning to an installer (not when unassigning)
    // For unassigned drops, installerDocument will be undefined (payroll "Unassign" won't match)
    const nonCompliantFlag = isComplianceHeld(
      installerDocument,
      weekDatesArray[droppedColumnIndex]
    );
    console.log(
      "JobInitModal useEffect[] Compliance installerDocument.Email: ",
      installerDocument?.Email,
      ", Scheduling_Hold_Effective_Date: ",
      installerDocument?.Scheduling_Hold_Effective_Date,
      ", targetDate: ",
      weekDatesArray[droppedColumnIndex],
      ", nonCompliantFlag: ",
      nonCompliantFlag
    );

    if (nonCompliantFlag) {
      console.log("JobInitModal useEffect[] ERROR non-compliant...");
      setOpenJobInitModal({ show: false });
      window.alert(COMPLIANCE_HOLD_ALERT);
      return;
    }

    // - - - - Compute new values WITHOUT mutating foundDocument (#85)
    const newValues = computeJobInitValues({
      foundDocument,
      dropObject,
      weekDatesArray,
    });

    // - - - - #154 Repair Order installer override
    // When the user has just answered the #154 confirmation modal for a
    // Repair Order with a pre-filled installer, JobInitModal opens with
    // a `repairOrderInstallerOverride` field on the open-payload. Apply
    // it on top of the computed installer so the chosen payroll# (Keep
    // the original vs. Reassign to drop target vs. Clear for unassigned-
    // clear) wins over computeJobInitValues' drop-target-based default.
    // `null` / `undefined` means "no override" (regular non-Repair-Order
    // flow runs unchanged).
    const repairOrderInstallerOverride = openJobInitModal?.repairOrderInstallerOverride;
    if (repairOrderInstallerOverride !== undefined && repairOrderInstallerOverride !== null) {
      newValues.installer = repairOrderInstallerOverride;
    }

    // - - - - ExtraCell side-effects (Attributes collection writes)
    // These are NOT mutations of foundDocument; they write to a separate
    // MongoDB collection via TanStack mutations.
    if (dropObject.id.slice(0, 10) === "Unassigned") {
      const extraCellId = dropObject.content?.props?.project?.id;
      const extraCellCustomText =
        dropObject.content?.props?.project?.encodedSmallTitle;

      if (dropObject.id === "Unassigned_RTS") {
        // Moving a job back to RTS: if it was married to an extraCell, re-create it.
        if (foundDocument.extraCellText && foundDocument.extraCellText !== "") {
          const originalScheduledDate = foundDocument.date_scheduled;
          const surfaceToUse =
            foundDocument.surface === "Carpet" ? "Carpet" : "Hard Surface";
          tsCreateExtraCellAttribute(
            originalScheduledDate,
            surfaceToUse,
            foundDocument.extraCellText
          );
        }
      } else if (extraCellCustomText && extraCellCustomText !== "") {
        // Moving into an extraCell on a day row: remove that cell's Attributes doc.
        tsRemoveExtraCellAttribute(extraCellId);
      }
    }

    // - - - - #193 over-capacity multi-day recommendation
    // When assigning to a specific installer (crew-cell drop sets a non-empty
    // installer), check whether the job's square footage exceeds that
    // installer's once/day capacity for the job's surface type(s). If so:
    //   1. surface a recommendation banner at the top of the modal, AND
    //   2. DEFAULT the Est-Complete date to span the recommended # of days
    //      (Brandon's #193 spec, 2026-06-09 — "default the Estimated Complete
    //      date to the latest date of the recommended number of days... The
    //      user still has the ability to change as they do today").
    // The default is a starting point only — the user can still freely edit
    // Est Complete. It does NOT block saving.
    let multiDayRecLocal = null;
    // #253 — the multi-day / over-capacity recommendation reads the installer's
    // capacity_*_once caps, which SCG candidates don't carry yet → gate it off for
    // SCG (no pop-up, no Est-Complete pre-fill; the user schedules normally).
    if (
      isCapacityAwareEnabled(companyCode) &&
      installerDocument &&
      newValues.installer
    ) {
      const rec = computeMultiDayJobRecommendation({
        job: foundDocument,
        candidate: installerDocument,
      });
      if (rec.exceedsCapacity) {
        multiDayRecLocal = rec;
        setMultiDayRec(rec);
        setInstallerName(
          installerDocument.Name_Full ||
            installerDocument.payrollNo ||
            newValues.installer
        );
      }
    }

    // - - - - Build a LOCAL display document (unmutated spread + new values)
    const displayDocument = { ...foundDocument, ...newValues };

    // - - - - Rescheduling detection
    // Parked job moves are intentional cross-week moves — skip reschedule reason requirement.
    const originalDateScheduled = foundDocument.date_scheduled;
    const originalDateMs = originalDateScheduled
      ? new Date(originalDateScheduled).getTime()
      : 0;
    const newDateMs = displayDocument.date_scheduled
      ? new Date(displayDocument.date_scheduled).getTime()
      : 0;
    if (
      originalDateScheduled &&
      originalDateMs !== newDateMs &&
      !isJobParked(dragObject)
    ) {
      setIsRescheduling(true);
    }

    console.log(
      "JobInitModal useEffect[] computed new values: ",
      newValues,
      ", displayDocument.date_scheduled: ",
      displayDocument.date_scheduled,
      ", displayDocument.statusCode: ",
      displayDocument.statusCode
    );

    // add to local state (these are LOCAL copies — foundDocument is untouched)
    setJobDocument(displayDocument);
    // #193 — when over capacity, default Est Complete to span the recommended
    // number of days; otherwise keep the computed/preserved completion date.
    const recommendedEstComplete = multiDayRecLocal
      ? computeRecommendedEstComplete(
          displayDocument.date_scheduled,
          multiDayRecLocal.recommendedDays
        )
      : "";
    setCurrentEstComplete(
      recommendedEstComplete || displayDocument?.date_estimatedCompletion
    );
    setCurrentArrivalTime(displayDocument?.arrival_time_text);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // - - - - - - - - - -

  return (
    <Modal onClose={() => setOpenJobInitModal({ show: false })}>
      <Modal.Header>
        Modifying Job#{" "}
        <span className="font-bold">{jobDocument.lowes_projectNo}</span>
      </Modal.Header>
      <Modal.Content>
        <div className="flex flex-col gap-4">
          {/* #193 — over-capacity multi-day advisory (read-only) */}
          {multiDayRec && multiDayRec.exceedsCapacity && (
            <div
              data-testid="multi-day-advisory"
              className="w-96 p-3 text-sm rounded-sm"
              style={{
                border: "2px solid #FDB824",
                backgroundColor: "rgba(253, 184, 36, 0.12)",
              }}
            >
              <div className="font-bold mb-1">
                Per {installerName ? `${installerName}'s` : "this installer's"}{" "}
                capacity, we recommend this job be set for{" "}
                {multiDayRec.recommendedDays} day
                {multiDayRec.recommendedDays === 1 ? "" : "s"}.
              </div>
              <div>
                This {multiDayRec.bindingType.type} job is{" "}
                <span className="font-bold">
                  {Math.round(multiDayRec.bindingType.jobSqft).toLocaleString()}{" "}
                  sq ft
                </span>
                , which exceeds the{" "}
                <span className="font-bold">
                  {Math.round(multiDayRec.bindingType.onceCap).toLocaleString()}{" "}
                  sq ft
                </span>{" "}
                once/day capacity (
                {Math.round(multiDayRec.bindingType.jobSqft).toLocaleString()} ÷{" "}
                {Math.round(multiDayRec.bindingType.onceCap).toLocaleString()} ={" "}
                {multiDayRec.recommendedDays}). <span className="italic">Est
                Complete</span> below has been set to span{" "}
                {multiDayRec.recommendedDays} days — change it if needed.
              </div>
            </div>
          )}
          <div className="flex flex-wrap w-96">
            <div className="flex-1 basis-2/4">Invoice#:</div>
            <div className="flex-1 basis-2/4 font-bold">
              {jobDocument.invoiceNo}
            </div>
          </div>
          <div className="flex flex-wrap w-96">
            <div className="flex-1 basis-2/4">Classification:</div>
            <div className="flex-1 basis-2/4 font-bold">
              {jobDocument.classification}
            </div>
          </div>
          <div className="flex flex-wrap w-96">
            <div className="flex-1 basis-2/4">Customer:</div>
            <div className="flex-1 basis-2/4 font-bold">
              {jobDocument.customer_lastName}, {jobDocument.customer_firstName}
            </div>
          </div>
          <div className="flex flex-wrap w-96">
            <div className="flex-1 basis-2/4">StoreNo:</div>
            <div className="flex-1 basis-2/4 font-bold">
              {jobDocument.lowes_storeNo}
            </div>
          </div>
          <div className="flex flex-wrap w-96">
            <div className="flex-1 basis-2/4">Installer:</div>
            <div className="flex-1 basis-2/4 font-bold">
              {jobDocument.installer}
            </div>
          </div>
          <div className="flex flex-wrap w-96">
            <div className="flex-1 basis-2/4">Scheduled:</div>
            <div className="flex-1 basis-2/4 font-bold">
              {jobDocument.date_scheduled}
            </div>
          </div>
          <div className="flex flex-wrap w-96">
            <div className="flex-1 basis-2/4">Est Complete</div>
            <div className="flex-1 basis-2/4 font-bold">
              <DatePicker
                selected={toDatePickerValue(currentEstComplete)}
                onChange={(date) => onEstCompleteChange(date)}
                className=""
                customInput={
                  <EstCompleteInput
                    className={`w-28 h-6 bg-crew-surface-hard border-l border-l-black/20`}
                  />
                }
                dateFormat="M/d/yyyy"
              />
            </div>
          </div>
          <div className="flex flex-wrap w-96">
            <div className="flex-1 basis-2/4">Arrival</div>
            <div className="flex-1 basis-2/4 font-bold">
              <select
                className="bg-yellow-30 text-black text-sm font-medium hover:opacity-75 cursor-pointer"
                value={currentArrivalTime || ""}
                onChange={(e) => onArrivalChange(e.target.value)}
              >
                {/* #261 — when no arrival is selected yet, show an explicit
                    placeholder (mirrors the Reason dropdown's "-- Select
                    Reason --") so the required-field block is comprehensible.
                    Previously value was undefined: the dropdown visually
                    defaulted to "8 AM - 11 AM" while state stayed undefined,
                    and the job saved with an empty arrival. */}
                {isArrivalTimeMissing(currentArrivalTime) && (
                  <option value="">-- Select Arrival --</option>
                )}
                <option value="8 AM - 11 AM">8 AM - 11 AM</option>
                <option value="11 AM - 2 PM">11 AM - 2 PM</option>
                <option value="2 PM - 5 PM">2 PM - 5 PM</option>
                {/* Preserve any non-standard existing arrival value so it stays
                    selectable / visible for already-scheduled jobs. */}
                {!isArrivalTimeMissing(currentArrivalTime) &&
                  !["8 AM - 11 AM", "11 AM - 2 PM", "2 PM - 5 PM"].includes(
                    currentArrivalTime
                  ) && (
                    <option value={currentArrivalTime}>
                      {currentArrivalTime}
                    </option>
                  )}
              </select>
            </div>
          </div>
          {isRescheduling && (
            <div className="flex flex-wrap w-96">
              <div className="flex-1 basis-2/4">Reason</div>
              <div className="flex-1 basis-2/4 font-bold">
                <select
                  className="bg-yellow-300 text-black text-sm font-medium hover:opacity-75 cursor-pointer p-2 h-auto w-full border border-black/20"
                  value={rescheduleReason}
                  onChange={(e) => setRescheduleReason(e.target.value)}
                >
                  <option value="">-- Select Reason --</option>
                  {RESCHEDULE_REASON_OPTIONS.map((reason) => (
                    <option key={reason} value={reason}>
                      {reason}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      </Modal.Content>
      <Modal.Footer>
        <div className="h-8 flex gap-4">
          <Button className="uppercase w-31" onClick={() => onSave()}>
            Save
          </Button>
          <Button
            color="light"
            className="uppercase w-31"
            onClick={() => setOpenJobInitModal({ show: false })}
          >
            Cancel
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default JobInitModal;
